const API_BASE = import.meta.env.VITE_API_URL || "";

/**
 * Get the current instance slug for API requests.
 * Sources (in priority order):
 * 1. VITE_INSTANCE_SLUG env var (build-time)
 * 2. URL path segment /i/:slug/
 * 3. null (no instance — legacy mode)
 */
function getInstanceSlug(): string | null {
  // Build-time override
  const envSlug = import.meta.env.VITE_INSTANCE_SLUG;
  if (envSlug) return envSlug;

  // URL query param: ?instance=<slug>
  const params = new URLSearchParams(window.location.search);
  const querySlug = params.get("instance");
  if (querySlug) return querySlug;

  // URL path: /i/:slug/...
  const match = window.location.pathname.match(/^\/i\/([a-z0-9][a-z0-9-]+[a-z0-9])\//);
  if (match) return match[1];

  // Fallback: instance admin session (used by /write page where URL has no ?instance=)
  // Only apply on /write — do NOT bleed into B2C pages where an admin may also be logged in.
  if (window.location.pathname === '/write') {
    try {
      const stored = localStorage.getItem("postcash_instance_user");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.instanceSlug) return parsed.instanceSlug;
      }
    } catch { /* ignore */ }
  }

  return null;
}

/** Shape returned by the `get_cheques` instance-admin action. */
export interface AdminCheque {
  id: string;
  short_id?: string | null;
  amount: string;
  token?: string | null;
  status: string;
  source?: string | null;
  funded?: boolean;
  created_at: string;
  claimed_at?: string | null;
  pin_attempts?: number;
  actual_balance_raw?: string | null;
  overfunded_at?: string | null;
}

interface ApiFetchOptions extends RequestInit {
  /** Skip sending X-Instance-Slug header (for admin/platform calls that pass instanceId in body). */
  skipInstanceSlug?: boolean;
  /** Correlate a chain of requests via X-Trace-Id header. See docs/plans/changelly-withdraw-test-flow-spec.md §3. */
  traceId?: string;
}

async function apiFetch<T = any>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { skipInstanceSlug, traceId, ...fetchOptions } = options;
  const instanceSlug = skipInstanceSlug ? null : getInstanceSlug();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers as Record<string, string> || {}),
  };
  if (instanceSlug) {
    headers["X-Instance-Slug"] = instanceSlug;
  }
  if (traceId) {
    headers["X-Trace-Id"] = traceId;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...fetchOptions,
    headers,
  });
  const data = await res.json();
  if (!res.ok) {
    // Prefer human-readable `message` over machine-code `error` for Error.message
    // (so toasts/inline text show "Cheque balance has not been confirmed yet"
    // instead of "balance_unknown"). `err.code` / `err.data` still expose raw codes.
    const err: any = new Error(data.message || data.error || "Request failed");
    err.status = res.status;
    err.code = data.code;
    err.data = data;
    throw err;
  }
  return data as T;
}

/**
 * Secure session handler — registered by the Admin React tree via setSecureSessionHandler().
 * When an admin API call fails with secure_session_required/expired, adminApiFetch calls this
 * handler (which triggers passkey re-auth), then retries the original request automatically.
 */
let _secureSessionHandler: (() => Promise<void>) | null = null;
let _pendingSecureSession: Promise<void> | null = null;

export function setSecureSessionHandler(handler: (() => Promise<void>) | null) {
  _secureSessionHandler = handler;
}

let _merchantSecureSessionHandler: (() => Promise<void>) | null = null;
let _pendingMerchantSecureSession: Promise<void> | null = null;

export function setMerchantSecureSessionHandler(handler: (() => Promise<void>) | null) {
  _merchantSecureSessionHandler = handler;
}

let _instanceSecureSessionHandler: (() => Promise<void>) | null = null;
let _pendingInstanceSecureSession: Promise<void> | null = null;

export function setInstanceSecureSessionHandler(handler: (() => Promise<void>) | null) {
  _instanceSecureSessionHandler = handler;
}

/**
 * Admin-scoped raw fetch: same retry logic as adminApiFetch but returns the raw Response
 * (for binary/non-JSON endpoints like batch export).
 */
async function adminApiFetchRaw(path: string, options: ApiFetchOptions = {}): Promise<Response> {
  const doFetch = () => {
    const token = localStorage.getItem("postcash_admin_token") || "";
    const { skipInstanceSlug, ...fetchOptions } = options;
    const instanceSlug = skipInstanceSlug ? null : getInstanceSlug();
    const headers: Record<string, string> = {
      ...(fetchOptions.headers as Record<string, string> || {}),
      Authorization: `Bearer ${token}`,
    };
    if (instanceSlug) headers["X-Instance-Slug"] = instanceSlug;
    return fetch(`${API_BASE}${path}`, { ...fetchOptions, headers });
  };

  const res = await doFetch();
  if ((res.status === 401 || res.status === 403) && _secureSessionHandler) {
    // Check if it's a secure_session error (returned as 403 by checkSecureSession)
    const clone = res.clone();
    try {
      const data = await clone.json();
      if (data.code === "secure_session_required" || data.code === "secure_session_expired") {
        if (!_pendingSecureSession) {
          _pendingSecureSession = _secureSessionHandler().finally(() => { _pendingSecureSession = null; });
        }
        await _pendingSecureSession;
        return await doFetch();
      }
    } catch { /* not JSON, fall through */ }
  }
  return res;
}

/**
 * Admin-scoped fetch: reads token from localStorage, adds Authorization header,
 * and auto-retries once after passkey re-auth if the server returns
 * secure_session_required or secure_session_expired.
 */
async function adminApiFetch<T = any>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const doFetch = () => {
    const token = localStorage.getItem("postcash_admin_token") || "";
    const existingHeaders = (options.headers as Record<string, string>) || {};
    return apiFetch<T>(path, {
      ...options,
      headers: { ...existingHeaders, Authorization: `Bearer ${token}` },
    });
  };

  try {
    return await doFetch();
  } catch (err: any) {
    if (
      (err.code === "secure_session_required" || err.code === "secure_session_expired") &&
      _secureSessionHandler
    ) {
      // De-duplicate: if re-auth is already in progress, wait for it instead of starting another
      if (!_pendingSecureSession) {
        _pendingSecureSession = _secureSessionHandler().finally(() => {
          _pendingSecureSession = null;
        });
      }
      await _pendingSecureSession;
      return await doFetch(); // retry with freshly stored token
    }
    throw err;
  }
}

async function merchantApiFetch<T = any>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const doFetch = () => {
    const token = localStorage.getItem("postcash_merchant_token") || "";
    const existingHeaders = (options.headers as Record<string, string>) || {};
    return apiFetch<T>(path, {
      ...options,
      headers: { ...existingHeaders, Authorization: `Bearer ${token}` },
    });
  };

  try {
    return await doFetch();
  } catch (err: any) {
    if (
      (err.code === "secure_session_required" || err.code === "secure_session_expired") &&
      _merchantSecureSessionHandler
    ) {
      if (!_pendingMerchantSecureSession) {
        _pendingMerchantSecureSession = _merchantSecureSessionHandler().finally(() => {
          _pendingMerchantSecureSession = null;
        });
      }
      await _pendingMerchantSecureSession;
      return await doFetch();
    }
    throw err;
  }
}

async function instanceApiFetch<T = any>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const doFetch = () => {
    const token = localStorage.getItem("postcash_instance_token") || "";
    const existingHeaders = (options.headers as Record<string, string>) || {};
    // NOTE: do NOT skipInstanceSlug here — instance auth calls must send X-Instance-Slug
    // so the backend can validate that the JWT belongs to the requested instance.
    return apiFetch<T>(path, {
      ...options,
      headers: { ...existingHeaders, Authorization: `Bearer ${token}` },
    });
  };

  try {
    return await doFetch();
  } catch (err: any) {
    if (
      (err.code === "secure_session_required" || err.code === "secure_session_expired") &&
      _instanceSecureSessionHandler
    ) {
      if (!_pendingInstanceSecureSession) {
        _pendingInstanceSecureSession = _instanceSecureSessionHandler().finally(() => {
          _pendingInstanceSecureSession = null;
        });
      }
      await _pendingInstanceSecureSession;
      return await doFetch();
    }
    // Stale/missing/invalidated instance JWT → clear it and bounce to login.
    // IssuePage's mount check only guards against a missing token; an expired token
    // (24h TTL) passes that check and then fails server-side with 401 no_auth,
    // surfacing as a generic error with no prompt.
    if (err?.status === 401 && (err.code === "no_auth" || err.code === "session_invalidated")) {
      try {
        localStorage.removeItem("postcash_instance_token");
      } catch { /* ignore */ }
      if (typeof window !== "undefined") {
        const slug = getInstanceSlug();
        const target = `/instance/login${slug ? `?instance=${encodeURIComponent(slug)}` : ""}`;
        // Avoid redirect loop if we're already on the login page.
        if (!window.location.pathname.startsWith("/instance/login")) {
          window.location.replace(target);
        }
      }
    }
    throw err;
  }
}

export const api = {
  invoke(functionName: string, body: unknown, opts?: { traceId?: string }) {
    return apiFetch(`/api/${functionName}`, {
      method: "POST",
      body: JSON.stringify(body),
      traceId: opts?.traceId,
    });
  },

  invokeAdmin(functionName: string, body: unknown, instanceId?: string) {
    const merged = instanceId ? { ...(body as any), instanceId } : body;
    return adminApiFetch(`/api/${functionName}`, {
      method: "POST",
      body: JSON.stringify(merged),
      skipInstanceSlug: true,
    });
  },

  getCheque(id: string, opts?: { withRedirectPacked?: boolean }) {
    const params = opts?.withRedirectPacked ? "?rp=1" : "";
    return apiFetch(`/api/cheques/${id}${params}`);
  },

  createCheque(cheque: Record<string, unknown>) {
    return apiFetch("/api/cheques", {
      method: "POST",
      body: JSON.stringify(cheque),
    });
  },

  oprfEvaluate(id: string, blinded: string): Promise<{ evaluation: string }> {
    return apiFetch(`/api/cheques/${id}/oprf-eval`, {
      method: "POST",
      body: JSON.stringify({ blinded }),
    });
  },

  uploadBlob(
    id: string,
    payload: { encrypted_private_key: string; redirect_packed?: string; funding_token: string },
  ): Promise<{ ok: true }> {
    return apiFetch(`/api/cheques/${id}/blob`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  // Admin-issued variant: sends the instance Bearer token and auto-retries once after
  // passkey re-auth if the backend returns secure_session_{required,expired}.
  createAdminCheque(cheque: Record<string, unknown>) {
    return instanceApiFetch("/api/cheques", {
      method: "POST",
      body: JSON.stringify({ ...cheque, source: "admin_issued" }),
    });
  },

  admin: {
    login(email: string, password: string, fp?: { visitorId: string; requestId: string }) {
      return apiFetch("/api/admin/login", {
        method: "POST",
        body: JSON.stringify({ email, password, fpVisitorId: fp?.visitorId, fpRequestId: fp?.requestId }),
        skipInstanceSlug: true,
      });
    },

    getMe() {
      const token = localStorage.getItem("postcash_admin_token") || "";
      return apiFetch("/api/admin/me", {
        headers: { Authorization: `Bearer ${token}` },
        skipInstanceSlug: true,
      });
    },

    logout() {
      localStorage.removeItem("postcash_admin_token");
    },

    passkeySetup(setupToken: string, credential: any) {
      return apiFetch("/api/admin/passkey-setup", {
        method: "POST",
        body: JSON.stringify({ setupToken, credential }),
        skipInstanceSlug: true,
      });
    },

    verifySetupToken(setupToken: string) {
      return apiFetch("/api/admin/setup", {
        method: "POST",
        body: JSON.stringify({ setupToken }),
        skipInstanceSlug: true,
      });
    },

    generateSetupLink(userId: string) {
      return adminApiFetch("/api/admin-wallet", {
        method: "POST",
        body: JSON.stringify({ action: "generate_admin_setup_link", userId }),
        skipInstanceSlug: true,
      });
    },

    passkeyDiscoverBegin() {
      return apiFetch("/api/admin/passkey-discover/begin", {
        method: "POST",
        body: JSON.stringify({}),
        skipInstanceSlug: true,
      });
    },

    passkeyDiscoverComplete(challengeToken: string, credential: any, fp?: { visitorId: string; requestId: string }) {
      return apiFetch("/api/admin/passkey-discover/complete", {
        method: "POST",
        body: JSON.stringify({ challengeToken, credential, fpVisitorId: fp?.visitorId, fpRequestId: fp?.requestId }),
        skipInstanceSlug: true,
      });
    },

    passkeyVerify(verifyToken: string, credential: any, challengeId: string) {
      return apiFetch("/api/admin/passkey-verify", {
        method: "POST",
        body: JSON.stringify({ verifyToken, credential, challengeId }),
        skipInstanceSlug: true,
      });
    },

    /** Dev-only: bypass passkey verification with a pending token (ADMIN_SKIP_PASSKEY=true). */
    devBypass(pendingToken: string) {
      return apiFetch("/api/admin/dev-bypass", {
        method: "POST",
        body: JSON.stringify({ pendingToken }),
        skipInstanceSlug: true,
      });
    },

    /** Get a WebAuthn challenge for secure session re-auth. */
    secureChallenge() {
      const token = localStorage.getItem("postcash_admin_token") || "";
      return apiFetch("/api/admin/secure-challenge", {
        method: "POST",
        body: JSON.stringify({}),
        headers: { Authorization: `Bearer ${token}` },
        skipInstanceSlug: true,
      });
    },

    /** Verify passkey assertion and receive a new JWT with fresh secureAt claim. */
    secureSession(credential: any, challengeId: string) {
      const token = localStorage.getItem("postcash_admin_token") || "";
      return apiFetch("/api/admin/secure-session", {
        method: "POST",
        body: JSON.stringify({ credential, challengeId }),
        headers: { Authorization: `Bearer ${token}` },
        skipInstanceSlug: true,
      });
    },

    listPasskeys() {
      const token = localStorage.getItem("postcash_admin_token") || "";
      return apiFetch("/api/admin/me/passkeys", {
        headers: { Authorization: `Bearer ${token}` },
        skipInstanceSlug: true,
      });
    },

    beginAddPasskey() {
      const token = localStorage.getItem("postcash_admin_token") || "";
      return apiFetch("/api/admin/me/passkeys/begin", {
        method: "POST",
        body: JSON.stringify({}),
        headers: { Authorization: `Bearer ${token}` },
        skipInstanceSlug: true,
      });
    },

    completeAddPasskey(credential: any) {
      const token = localStorage.getItem("postcash_admin_token") || "";
      return apiFetch("/api/admin/me/passkeys/complete", {
        method: "POST",
        body: JSON.stringify({ credential }),
        headers: { Authorization: `Bearer ${token}` },
        skipInstanceSlug: true,
      });
    },

    deletePasskey(credId: string) {
      const token = localStorage.getItem("postcash_admin_token") || "";
      return apiFetch(`/api/admin/me/passkeys/${encodeURIComponent(credId)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        skipInstanceSlug: true,
      });
    },

    changePassword(currentPassword: string, newPassword: string) {
      const token = localStorage.getItem("postcash_admin_token") || "";
      return apiFetch("/api/admin/me/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
        headers: { Authorization: `Bearer ${token}` },
        skipInstanceSlug: true,
      });
    },
  },

  merchant: {
    login(email: string, password: string, fp?: { visitorId: string; requestId: string }) {
      return apiFetch("/api/b2b/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password, fpVisitorId: fp?.visitorId, fpRequestId: fp?.requestId }),
      });
    },

    getMe() {
      return merchantApiFetch("/api/b2b/auth/me");
    },

    logout() {
      localStorage.removeItem("postcash_merchant_token");
    },

    passkeySetup(setupToken: string, credential: any) {
      return apiFetch("/api/b2b/auth/passkey-setup", {
        method: "POST",
        body: JSON.stringify({ setupToken, credential }),
      });
    },

    passkeyVerify(verifyToken: string, credential: any, challengeId: string) {
      return apiFetch("/api/b2b/auth/passkey-verify", {
        method: "POST",
        body: JSON.stringify({ verifyToken, credential, challengeId }),
      });
    },

    secureChallenge() {
      return merchantApiFetch("/api/b2b/auth/secure-challenge", {
        method: "POST",
        body: JSON.stringify({}),
      });
    },

    secureSession(credential: any, challengeId: string) {
      return merchantApiFetch("/api/b2b/auth/secure-session", {
        method: "POST",
        body: JSON.stringify({ credential, challengeId }),
      });
    },

    passkeyLoginChallenge() {
      return apiFetch("/api/b2b/auth/passkey-login-challenge", {
        method: "POST",
        body: JSON.stringify({}),
      });
    },

    passkeyLogin(credentialData: any, challengeId: string, fp?: { visitorId: string; requestId: string }) {
      return apiFetch("/api/b2b/auth/passkey-login", {
        method: "POST",
        body: JSON.stringify({ credentialData, challengeId, fpVisitorId: fp?.visitorId, fpRequestId: fp?.requestId }),
      });
    },

    forgotPassword(email: string) {
      return apiFetch("/api/b2b/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    },

    resetPassword(token: string, newPassword: string) {
      return apiFetch("/api/b2b/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, newPassword }),
      });
    },
  },

  voidCheque(chequeId: string, reason?: string) {
    const token = localStorage.getItem("postcash_passkey_token") || "";
    return apiFetch("/api/void-cheque", {
      method: "POST",
      body: JSON.stringify({ chequeId, reason: reason || undefined }),
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  batches: {
    create(data: { denomination: number; quantity: number; merchant_id?: string; expires_days?: number; type?: "physical" | "digital"; auto_ship?: boolean }) {
      return adminApiFetch("/api/batches", {
        method: "POST",
        body: JSON.stringify(data),
        skipInstanceSlug: true,
      });
    },
    list(params?: { page?: number; limit?: number; status?: string; instanceId?: string }) {
      const qs = new URLSearchParams();
      if (params?.page) qs.set("page", String(params.page));
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.status) qs.set("status", params.status);
      if (params?.instanceId) qs.set("instance_id", params.instanceId);
      return adminApiFetch(`/api/batches?${qs}`, {
        skipInstanceSlug: true,
      });
    },
    get(id: string) {
      return adminApiFetch(`/api/batches/${id}`, {
        skipInstanceSlug: true,
      });
    },
    ship(id: string) {
      return adminApiFetch(`/api/batches/${id}/ship`, {
        method: "POST",
        skipInstanceSlug: true,
      });
    },
    voidBatch(id: string, reason?: string) {
      return adminApiFetch(`/api/batches/${id}/void`, {
        method: "POST",
        body: JSON.stringify({ reason }),
        skipInstanceSlug: true,
      });
    },
    statsOverview(instanceId?: string) {
      const qs = instanceId ? `?instance_id=${instanceId}` : "";
      return adminApiFetch(`/api/batches/stats/overview${qs}`, {
        skipInstanceSlug: true,
      });
    },
    export(id: string) {
      return adminApiFetchRaw(`/api/batches/${id}/export`, { skipInstanceSlug: true });
    },
    getPgpKey() {
      return adminApiFetch("/api/batches/pgp-key", {
        skipInstanceSlug: true,
      });
    },
    setPgpKey(publicKey: string) {
      return adminApiFetch("/api/batches/pgp-key", {
        method: "POST",
        body: JSON.stringify({ public_key: publicKey }),
        skipInstanceSlug: true,
      });
    },
    getFundingWallet() {
      return adminApiFetch("/api/batches/funding-wallet", {
        skipInstanceSlug: true,
      });
    },
    setupFundingWallet(action: "generate" | "import", privateKey?: string) {
      return adminApiFetch("/api/batches/funding-wallet", {
        method: "POST",
        body: JSON.stringify({ action, private_key: privateKey }),
        skipInstanceSlug: true,
      });
    },
    demoTopup(amount: number) {
      return adminApiFetch("/api/batches/demo-topup", {
        method: "POST",
        body: JSON.stringify({ amount }),
        skipInstanceSlug: true,
      });
    },
    recordPayment(data: { merchant_id: string; amount: number; currency?: string; method?: string; reference?: string; notes?: string }) {
      return adminApiFetch("/api/batches/payments", {
        method: "POST",
        body: JSON.stringify(data),
        skipInstanceSlug: true,
      });
    },
    getMerchantPayments(merchantId: string) {
      return adminApiFetch(`/api/batches/payments/${merchantId}`, {
        skipInstanceSlug: true,
      });
    },
    deletePgpKey() {
      return adminApiFetch("/api/batches/pgp-key", {
        method: "DELETE",
        skipInstanceSlug: true,
      });
    },
    listCheques(batchId: string) {
      return adminApiFetch(`/api/batches/${batchId}/cheques`, { skipInstanceSlug: true });
    },
    revealCheque(batchId: string, chequeId: string) {
      return adminApiFetch(`/api/batches/${batchId}/reveal-cheque`, {
        method: "POST",
        body: JSON.stringify({ chequeId }),
        skipInstanceSlug: true,
      });
    },
  },

  activate(chequeId: string, instanceSlug?: string | null) {
    const extraHeaders: Record<string, string> = {};
    if (instanceSlug) extraHeaders["X-Instance-Slug"] = instanceSlug;
    return apiFetch("/api/activate", {
      method: "POST",
      body: JSON.stringify({ chequeId }),
      headers: extraHeaders,
    });
  },

  activationStatus(chequeId: string, instanceSlug?: string | null) {
    const extraHeaders: Record<string, string> = {};
    if (instanceSlug) extraHeaders["X-Instance-Slug"] = instanceSlug;
    return apiFetch(`/api/activate/status/${chequeId}`, { headers: extraHeaders });
  },

  invokeMerchant(path: string, body: unknown) {
    return merchantApiFetch(`/api/b2b/${path}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  instanceAuth: {
    login(email: string, password: string, instanceSlug: string, fp?: { visitorId: string; requestId: string }) {
      return apiFetch("/api/instance/auth/login", {
        method: "POST",
        body: JSON.stringify({
          email, password, instanceSlug,
          fpVisitorId: fp?.visitorId || undefined,
          fpRequestId: fp?.requestId || undefined,
        }),
        skipInstanceSlug: true,
      });
    },

    me() {
      const token = localStorage.getItem("postcash_instance_token") || "";
      return apiFetch("/api/instance/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
        skipInstanceSlug: true,
      });
    },

    logout() {
      localStorage.removeItem("postcash_instance_token");
    },

    setupVerifyToken(token: string) {
      return apiFetch("/api/instance/auth/setup", {
        method: "POST",
        body: JSON.stringify({ action: "verify_token", token }),
        skipInstanceSlug: true,
      });
    },

    setupComplete(token: string, newPassword: string) {
      return apiFetch("/api/instance/auth/setup", {
        method: "POST",
        body: JSON.stringify({ action: "complete", token, newPassword }),
        skipInstanceSlug: true,
      });
    },

    passkeySetup(setupToken: string, credential: any) {
      return apiFetch("/api/instance/auth/passkey-setup", {
        method: "POST",
        body: JSON.stringify({ setupToken, credential }),
        skipInstanceSlug: true,
      });
    },

    passkeyVerify(verifyToken: string, credential: any, challengeId: string) {
      return apiFetch("/api/instance/auth/passkey-verify", {
        method: "POST",
        body: JSON.stringify({ verifyToken, credential, challengeId }),
        skipInstanceSlug: true,
      });
    },

    forgotPassword(email: string, instanceSlug: string) {
      return apiFetch("/api/instance/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email, instanceSlug }),
        skipInstanceSlug: true,
      });
    },

    resetPassword(token: string, newPassword: string) {
      return apiFetch("/api/instance/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, newPassword }),
        skipInstanceSlug: true,
      });
    },

    secureChallenge() {
      return instanceApiFetch("/api/instance/auth/secure-challenge", {
        method: "POST",
        body: JSON.stringify({}),
      });
    },

    secureSession(credential: any, challengeId: string) {
      return instanceApiFetch("/api/instance/auth/secure-session", {
        method: "POST",
        body: JSON.stringify({ credential, challengeId }),
      });
    },

    secureSessionPassword(password: string) {
      return instanceApiFetch("/api/instance/auth/secure-session-password", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
    },

    beginPasskeyDiscover(instanceSlug: string) {
      return apiFetch("/api/instance/auth/passkey-discover/begin", {
        method: "POST",
        body: JSON.stringify({ instanceSlug }),
        skipInstanceSlug: true,
      });
    },

    completePasskeyDiscover(challengeToken: string, credential: any, fp?: { visitorId: string; requestId: string }) {
      return apiFetch("/api/instance/auth/passkey-discover/complete", {
        method: "POST",
        body: JSON.stringify({
          challengeToken, credential,
          fpVisitorId: fp?.visitorId || undefined,
          fpRequestId: fp?.requestId || undefined,
        }),
        skipInstanceSlug: true,
      });
    },

    listPasskeys() {
      return instanceApiFetch("/api/instance/auth/me/passkeys", { method: "GET" });
    },

    beginAddPasskey() {
      return instanceApiFetch("/api/instance/auth/me/passkeys/begin", { method: "POST", body: JSON.stringify({}) });
    },

    completeAddPasskey(credential: any) {
      return instanceApiFetch("/api/instance/auth/me/passkeys/complete", {
        method: "POST",
        body: JSON.stringify({ credential }),
      });
    },

    deletePasskey(credId: string) {
      return instanceApiFetch(`/api/instance/auth/me/passkeys/${encodeURIComponent(credId)}`, { method: "DELETE" });
    },

    changePassword(currentPassword: string, newPassword: string) {
      return instanceApiFetch("/api/instance/auth/me/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
    },

    loginHistory() {
      return instanceApiFetch("/api/instance/auth/me/login-history", { method: "GET" });
    },
  },

  invokeInstanceAdmin(body: unknown) {
    return instanceApiFetch("/api/instance-admin", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  billing: {
    async downloadInvoicePdf(invoiceId: string, instanceId?: string, useInstanceApi = false): Promise<void> {
      const token = useInstanceApi
        ? (localStorage.getItem("postcash_instance_token") || "")
        : (localStorage.getItem("postcash_admin_token") || "");
      const body = instanceId
        ? { action: "download_invoice_pdf", invoiceId, instanceId }
        : { action: "download_invoice_pdf", invoiceId };

      const res = await fetch("/api/instance-admin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as any;
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }

      const data = await res.json() as { pdf: string; filename: string };
      const binaryStr = atob(data.pdf);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    async fetchInvoicePdfBlobUrl(invoiceId: string, instanceId?: string, useInstanceApi = false): Promise<string> {
      const token = useInstanceApi
        ? (localStorage.getItem("postcash_instance_token") || "")
        : (localStorage.getItem("postcash_admin_token") || "");
      const body = instanceId
        ? { action: "download_invoice_pdf", invoiceId, instanceId }
        : { action: "download_invoice_pdf", invoiceId };

      const res = await fetch("/api/instance-admin", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as any;
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json() as { pdf: string; filename: string };
      const binaryStr = atob(data.pdf);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      return URL.createObjectURL(blob);
    },
  },

  print: {
    getConfig(batchId: string) {
      return instanceApiFetch("/api/instance-admin", {
        method: "POST",
        body: JSON.stringify({ action: "get_print_config", batchId }),
      });
    },
    saveConfig(batchId: string, data: {
      pdfData?: string;
      pageWidth: number;
      pageHeight: number;
      pdfDataInsert?: string;
      pageWidthInsert?: number;
      pageHeightInsert?: number;
      fields: import("../components/print/types").FieldPlacement[];
      sheetConfig: import("../components/print/types").SheetConfig;
      pkgFrontEnabled?: boolean;
      pkgBackEnabled?: boolean;
      insFrontEnabled?: boolean;
      insBackEnabled?: boolean;
      pkgPagesSwapped?: boolean;
      insPagesSwapped?: boolean;
    }) {
      return instanceApiFetch("/api/instance-admin", {
        method: "POST",
        body: JSON.stringify({ action: "save_print_config", batchId, ...data }),
      });
    },
    generate(batchId: string, preview = false) {
      return instanceApiFetch("/api/instance-admin", {
        method: "POST",
        body: JSON.stringify({ action: "generate_print_sheet", batchId, preview }),
      });
    },
    importConfig(sourceBatchId: string, targetBatchId: string) {
      return instanceApiFetch("/api/instance-admin", {
        method: "POST",
        body: JSON.stringify({ action: "import_print_config", sourceBatchId, targetBatchId }),
      });
    },
    listConfigs() {
      return instanceApiFetch("/api/instance-admin", {
        method: "POST",
        body: JSON.stringify({ action: "list_print_configs" }),
      });
    },
    generateCards(batchId: string, preview = false) {
      return instanceApiFetch("/api/instance-admin", {
        method: "POST",
        body: JSON.stringify({ action: "generate_card_pdf", batchId, preview }),
      });
    },
  },
};
